import { createTheme } from '@mui/material/styles';

// Brand palette — aligned with admin/src/theme/index.js
const brand = {
  red: { main: '#A62639', dark: '#821E2D', light: '#D06577' },
  purple: { main: '#9B6BD4', dark: '#7748B0', light: '#C693FF' },
  blue: { main: '#4A87BD', dark: '#2E6A9E', light: '#7AADDA' },
  green: { main: '#4FA87A', dark: '#3B8C60', light: '#7DC7A0' },
  orange: { main: '#FA9741', dark: '#D47B25', light: '#FBB878' },
  yellow: { main: '#D4A017', dark: '#A87D10', light: '#E8C45A' },
};

const grey = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e0',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

const shadow = {
  a: (opacity) => `rgba(15, 23, 42, ${opacity})`,
};

const shadows = [
  'none',
  `0 1px 1px ${shadow.a(0.035)}, 0 1px 3px ${shadow.a(0.06)}`,
  `0 1px 2px ${shadow.a(0.04)}, 0 2px 6px -1px ${shadow.a(0.07)}`,
  `0 1px 2px ${shadow.a(0.04)}, 0 3px 8px -1px ${shadow.a(0.08)}`,
  `0 1px 2px ${shadow.a(0.04)}, 0 4px 10px -2px ${shadow.a(0.09)}`,
  `0 2px 4px -1px ${shadow.a(0.06)}, 0 4px 12px -2px ${shadow.a(0.1)}`,
  `0 2px 4px -1px ${shadow.a(0.06)}, 0 6px 16px -2px ${shadow.a(0.11)}`,
  `0 2px 4px -1px ${shadow.a(0.06)}, 0 8px 20px -3px ${shadow.a(0.12)}`,
  `0 2px 4px -1px ${shadow.a(0.06)}, 0 10px 24px -3px ${shadow.a(0.13)}`,
  `0 3px 6px -1px ${shadow.a(0.07)}, 0 10px 28px -3px ${shadow.a(0.13)}`,
  `0 3px 6px -1px ${shadow.a(0.07)}, 0 12px 32px -3px ${shadow.a(0.14)}`,
  `0 3px 6px -1px ${shadow.a(0.08)}, 0 14px 36px -4px ${shadow.a(0.15)}`,
  `0 3px 6px -1px ${shadow.a(0.08)}, 0 16px 40px -4px ${shadow.a(0.15)}`,
  `0 4px 8px -1px ${shadow.a(0.08)}, 0 16px 44px -4px ${shadow.a(0.16)}`,
  `0 4px 8px -1px ${shadow.a(0.08)}, 0 18px 48px -5px ${shadow.a(0.16)}`,
  `0 4px 8px -1px ${shadow.a(0.09)}, 0 20px 52px -5px ${shadow.a(0.17)}`,
  `0 4px 8px -1px ${shadow.a(0.09)}, 0 22px 56px -5px ${shadow.a(0.17)}`,
  `0 4px 8px -1px ${shadow.a(0.09)}, 0 24px 60px -6px ${shadow.a(0.18)}`,
  `0 4px 8px -1px ${shadow.a(0.1)}, 0 26px 64px -6px ${shadow.a(0.18)}`,
  `0 4px 8px -1px ${shadow.a(0.1)}, 0 28px 68px -6px ${shadow.a(0.19)}`,
  `0 4px 8px -1px ${shadow.a(0.1)}, 0 30px 72px -7px ${shadow.a(0.19)}`,
  `0 4px 8px -1px ${shadow.a(0.1)}, 0 32px 76px -7px ${shadow.a(0.2)}`,
  `0 4px 8px -1px ${shadow.a(0.11)}, 0 34px 80px -7px ${shadow.a(0.2)}`,
  `0 4px 8px -1px ${shadow.a(0.11)}, 0 36px 84px -8px ${shadow.a(0.2)}`,
  `0 4px 8px -1px ${shadow.a(0.11)}, 0 38px 88px -8px ${shadow.a(0.2)}`,
];

/** Simulation chat bubbles — light fill + saturated label (classic client palette). */
export const CHAT_SENDER_COLORS = {
  client: { bg: '#E3F2FD', text: '#1976D2' },
  patient: { bg: '#E8F5E8', text: '#2E7D32' },
  nurse: { bg: '#E1BEE7', text: '#7B1FA2' },
  software: { bg: '#FFE0B2', text: '#F57C00' },
  system: { bg: '#FFE0B2', text: '#F57C00' },
  super_doctor: { bg: '#B2DFDB', text: '#00695C' },
  default: { bg: '#F5F5F5', text: '#424242' },
};

/** Persona role accent colors (icons, cards, avatars). */
export const PERSONA_COLORS = {
  nurse: {
    main: brand.purple.main,
    bg: 'rgba(155, 107, 212, 0.14)',
    bgSelected: 'rgba(155, 107, 212, 0.22)',
  },
  software: {
    main: brand.orange.dark,
    bg: 'rgba(250, 151, 65, 0.14)',
    bgSelected: 'rgba(250, 151, 65, 0.22)',
  },
  doctor: {
    main: '#3DA89A',
    bg: 'rgba(61, 168, 154, 0.14)',
    bgSelected: 'rgba(61, 168, 154, 0.22)',
  },
};

/**
 * @param {string} sender
 * @returns {{ bg: string, text: string }}
 */
export function getChatBubbleColors(sender) {
  const key = String(sender || '').toLowerCase();
  if (key === 'client') return CHAT_SENDER_COLORS.client;
  if (key === 'patient') return CHAT_SENDER_COLORS.patient;
  if (key === 'nurse') return CHAT_SENDER_COLORS.nurse;
  if (key === 'system') return CHAT_SENDER_COLORS.system;
  if (key === 'software') return CHAT_SENDER_COLORS.software;
  if (key === 'super_doctor' || key === 'superdoctor') return CHAT_SENDER_COLORS.super_doctor;
  return CHAT_SENDER_COLORS.default;
}

/** Map /api/chat/target recipient to chat bubble color key. */
export function chatRecipientToSenderKey(recipient) {
  const key = String(recipient || '').toLowerCase();
  if (key === 'super_doctor' || key === 'superdoctor') return 'super_doctor';
  if (key === 'nurse') return 'nurse';
  if (key === 'software' || key === 'system') return 'software';
  if (key === 'patient') return 'patient';
  return 'default';
}

/** Icons in chat message headers use a single neutral color. */
export const CHAT_SENDER_ICON_SX = { color: '#000000' };

const theme = createTheme({
  shadows,
  palette: {
    primary: {
      main: brand.blue.main,
      dark: brand.blue.dark,
      light: brand.blue.light,
      contrastText: '#ffffff',
    },
    secondary: {
      main: brand.purple.main,
      dark: brand.purple.dark,
      light: brand.purple.light,
      contrastText: '#ffffff',
    },
    background: {
      default: grey[50],
      paper: '#ffffff',
      secondary: grey[100],
    },
    text: {
      primary: grey[800],
      secondary: grey[600],
    },
    success: {
      main: brand.green.main,
      dark: brand.green.dark,
      light: brand.green.light,
      contrastText: '#ffffff',
    },
    warning: {
      main: brand.orange.main,
      dark: brand.orange.dark,
      light: brand.orange.light,
      contrastText: '#ffffff',
    },
    error: {
      main: brand.red.main,
      dark: brand.red.dark,
      light: brand.red.light,
      contrastText: '#ffffff',
    },
    info: {
      main: brand.blue.light,
      dark: brand.blue.main,
      light: '#A3C7E8',
      contrastText: '#ffffff',
    },
    grey,
    medical: {
      emergency: brand.red.main,
      caution: brand.orange.main,
      success: brand.green.main,
      neutral: grey[500],
      accent: brand.purple.main,
      primary: brand.blue.main,
      secondary: grey[500],
    },
    persona: PERSONA_COLORS,
    chat: {
      senders: CHAT_SENDER_COLORS,
      userBubble: brand.blue.main,
      userText: '#ffffff',
      patientBubble: '#ffffff',
      patientText: grey[800],
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em', color: grey[800] },
    h2: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.015em', color: grey[800] },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3, color: grey[800] },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35, color: grey[800] },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4, color: grey[800] },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4, color: grey[800] },
    body1: { fontSize: '0.875rem', lineHeight: 1.5, color: grey[600] },
    body2: { fontSize: '0.8125rem', lineHeight: 1.43, color: grey[600] },
    button: { fontWeight: 500, textTransform: 'none', fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: grey[500] },
  },
  spacing: 8,
  shape: { borderRadius: 6 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { margin: 0, padding: 0, boxSizing: 'border-box' },
        body: {
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          backgroundColor: grey[50],
          fontSize: '0.875rem',
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        },
        '#root': { margin: 0, padding: 0, minHeight: '100vh' },
        '*': { boxSizing: 'border-box' },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width: 600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
        },
        contained: ({ theme: t }) => ({
          boxShadow: 'none',
          '&:hover': { boxShadow: t.shadows[2] },
          '&:active': { boxShadow: t.shadows[1] },
        }),
        outlined: {
          borderWidth: '1px',
          '&:hover': { borderWidth: '1px', boxShadow: 'none' },
        },
        text: { '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 8,
          border: `1px solid ${grey[200]}`,
          boxShadow: t.shadows[1],
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: '#ffffff',
            '& fieldset': { borderColor: grey[200] },
            '&:hover fieldset': { borderColor: brand.blue.main },
            '&.Mui-focused fieldset': { borderColor: brand.blue.main },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: brand.blue.main },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          backgroundColor: '#ffffff',
          color: grey[800],
          boxShadow: t.shadows[1],
          borderBottom: `1px solid ${grey[200]}`,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6 },
        standardInfo: {
          backgroundColor: 'rgba(74, 135, 189, 0.08)',
          borderColor: 'rgba(74, 135, 189, 0.25)',
          color: brand.blue.dark,
        },
        standardSuccess: {
          backgroundColor: 'rgba(79, 168, 122, 0.08)',
          borderColor: 'rgba(79, 168, 122, 0.25)',
          color: brand.green.dark,
        },
        standardWarning: {
          backgroundColor: 'rgba(250, 151, 65, 0.08)',
          borderColor: 'rgba(250, 151, 65, 0.25)',
          color: brand.orange.dark,
        },
        standardError: {
          backgroundColor: 'rgba(166, 38, 57, 0.08)',
          borderColor: 'rgba(166, 38, 57, 0.25)',
          color: brand.red.dark,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': { paddingBottom: 16 },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${grey[200]}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          borderColor: grey[200],
        },
        head: {
          fontWeight: 600,
          backgroundColor: grey[50],
          color: grey[700],
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { borderBottom: 0 },
          '&.MuiTableRow-hover:hover': { backgroundColor: grey[50] },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 48 },
        indicator: { height: 2 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 6 },
        sizeSmall: { fontSize: '0.75rem', height: 24 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.blue.main,
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontSize: '1.125rem', fontWeight: 600 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: 1700,
        },
        tooltip: {
          fontSize: '0.75rem',
          borderRadius: 4,
          backgroundColor: 'rgba(33, 33, 33, 0.98)',
          color: '#ffffff',
          maxWidth: 360,
          '& .MuiTypography-root': {
            color: '#ffffff',
          },
        },
        arrow: {
          color: 'rgba(33, 33, 33, 0.98)',
        },
      },
    },
  },
});

/** Reusable sx presets — mirror admin/src/theme adminSx for consistent patient UI. */
export const clientSx = {
  tableContainer: {
    border: `1px solid ${grey[200]}`,
    borderRadius: 2,
    boxShadow: 'none',
    '& .MuiTable-root': { tableLayout: 'auto' },
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
      fontWeight: 700,
      verticalAlign: 'middle',
    },
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: grey[50] },
    transition: 'background-color 0.15s ease',
  },
  tabBar: {
    mb: 3,
    borderBottom: 1,
    borderColor: 'divider',
    minHeight: 48,
  },
  sectionCard: {
    p: 2,
    mb: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'background.paper',
    boxShadow: 1,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 2,
    flexWrap: 'wrap',
  },
  emptyState: {
    py: 8,
    textAlign: 'center',
    color: 'text.secondary',
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    py: 8,
  },
  pageActionBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: (theme) => theme.zIndex.appBar,
    px: { xs: 2, sm: 3 },
    py: 1,
    borderTop: 1,
    borderColor: 'divider',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 1,
    bgcolor: 'background.paper',
    boxShadow: 4,
  },
};

export default theme;
